import { useEffect, useRef, useState } from "react";

export interface ExportMenuProps {
  /** zip 导出进行中：入口显示小圆点，避免用户以为点了没反应。 */
  busy?: boolean;
  /** 没有可导出的集时整块禁用。 */
  disabled?: boolean;
  disabledHint?: string;
  onExportZip: () => void;
  onPrint: () => void;
}

/**
 * 顶栏「导出」下拉：`导出 zip` / `打印故事板`（契约 M4 §3）。
 *
 * 交互底线：`aria-haspopup="menu"` + 菜单项 `role="menuitem"`，
 * Esc 关闭并把焦点还给入口，点外面也关（点完不会把菜单留在屏幕上）。
 */
export function ExportMenu({
  busy = false,
  disabled = false,
  disabledHint,
  onExportZip,
  onPrint,
}: ExportMenuProps) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const firstItemRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(() => firstItemRef.current?.focus(), 0);

    function handlePointerDown(event: MouseEvent) {
      if (!wrapRef.current?.contains(event.target as Node)) setOpen(false);
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        event.preventDefault();
        setOpen(false);
        triggerRef.current?.focus();
        return;
      }

      // role="menu" 的键盘约定：上下键在菜单项之间移动。
      if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
      const items = Array.from(
        wrapRef.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]') ?? [],
      );
      if (items.length === 0) return;
      event.preventDefault();
      const index = items.indexOf(document.activeElement as HTMLButtonElement);
      const step = event.key === "ArrowDown" ? 1 : -1;
      const next = index === -1 ? 0 : (index + step + items.length) % items.length;
      items[next]?.focus();
    }

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  function choose(handler: () => void) {
    setOpen(false);
    triggerRef.current?.focus();
    handler();
  }

  return (
    <div className="dp-export-menu" ref={wrapRef}>
      <button
        ref={triggerRef}
        type="button"
        className="dp-btn sm dp-export-trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="导出"
        disabled={disabled}
        title={disabled ? (disabledHint ?? "还没有可导出的内容") : "导出 zip / 打印故事板"}
        onClick={() => setOpen((value) => !value)}
      >
        <span className={`dp-export-dot${busy ? " is-busy" : ""}`} aria-hidden="true" />
        导出
        <span aria-hidden="true">▾</span>
      </button>

      {open ? (
        <div className="dp-export-pop" role="menu" aria-label="导出方式">
          <button
            ref={firstItemRef}
            type="button"
            role="menuitem"
            className="dp-export-item"
            onClick={() => choose(onExportZip)}
          >
            <span className="dp-export-item-name">导出 zip</span>
            <span className="dp-export-item-hint">剧本 + storyboard + 媒体，后台打包</span>
          </button>
          <button
            type="button"
            role="menuitem"
            className="dp-export-item"
            onClick={() => choose(onPrint)}
          >
            <span className="dp-export-item-name">打印故事板</span>
            <span className="dp-export-item-hint">浅色 A4 打印视图，浏览器「另存为 PDF」</span>
          </button>
        </div>
      ) : null}
    </div>
  );
}
